'use client'

import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import { BackButton } from '@/components/ui/BackButton'
import { useToast } from '@/components/ui/use-toast'
import { ErrorDisplay } from '@/components/ui/error-display'
import {
    TranslationDetails,
    useTranslation as useTranslationQuery,
    useUpdateTranslation,
    type UpdateTranslationInput,
} from '@/lib/api/translations'
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils'
import { translationFormSchema, type TranslationFormData } from '@/components/form/translation/validation'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { TranslationForm } from '@/components/form/translation/translation-form'
import { TranslationDetailsSkeleton } from './components/translation-details-skeleton'
import { TranslationDetailsNotFound } from './components/translation-details-not-found'

const parseTranslationToFormData = (translation: TranslationDetails): TranslationFormData => {
    return {
        locale: translation.locale || '',
        countryCode: translation.countryCode || '',
        messages: (translation.messages || {}) as Record<string, any>,
        enabled: translation.enabled ?? true,
    }
}

const TranslationDetailPageInner = () => {
    const { transId } = useParams<{ transId: string }>()
    if (!transId) {
        throw new Error('Translation ID is required')
    }

    const { data: translationData, isLoading, error } = useTranslationQuery(transId)
    if (isLoading) {
        return <TranslationDetailsSkeleton />
    }
    if (error) {
        return <ErrorDisplay error={parseTRPCError(error)} />
    }
    if (!translationData) {
        return <TranslationDetailsNotFound />
    }
    return <TranslationDetailContent translation={translationData.translation} />
}

interface TranslationDetailContentProps {
    translation: TranslationDetails
}

const TranslationDetailContent = ({ translation }: TranslationDetailContentProps) => {
    const { t } = useTranslation('languages')
    const { t: tCommon } = useTranslation('common')
    const { storeRoutes, navigate } = useStoreNavigate()
    const { toast } = useToast()

    const updateTranslationMutation = useUpdateTranslation()
    const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null)

    const formData = translation ? parseTranslationToFormData(translation) : null

    const methods = useForm<TranslationFormData>({
        defaultValues: formData || {
            locale: '',
            countryCode: '',
            messages: {},
            enabled: true,
        },
        resolver: zodResolver(translationFormSchema),
        mode: 'onBlur',
    })

    const {
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = methods

    React.useEffect(() => {
        if (translation) {
            reset(parseTranslationToFormData(translation))
        }
    }, [translation, reset])

    const handleUpdateTranslation = async (data: TranslationFormData) => {
        try {
            setFormError(null)

            const input: UpdateTranslationInput = {
                id: translation.id,
                data: {
                    locale: data.locale,
                    countryCode: data.countryCode,
                    messages: data.messages,
                    enabled: data.enabled,
                },
            }

            await updateTranslationMutation.mutateAsync(input)

            toast({
                title: tCommon('success'),
                description: t('updateSuccess'),
            })

            navigate(storeRoutes.TRANSLATIONS())
        } catch (error) {
            const parsedError = parseTRPCError(error)
            setFormError(parsedError)

            if (!parsedError.isValidationError) {
                toast({
                    title: tCommon('error'),
                    description: parsedError.message,
                    variant: 'destructive',
                })
            }
        }
    }

    return (
        <div className="bg-background min-h-screen">
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(handleUpdateTranslation)}>
                        {/* Header */}
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-center gap-4">
                                <BackButton to={storeRoutes.TRANSLATIONS()} />
                                <div>
                                    <h1 className="text-2xl font-bold tracking-tight">{t('editTranslation')}</h1>
                                    <p className="text-muted-foreground mt-1 text-sm">{t('editDescription')}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(storeRoutes.TRANSLATIONS())}
                                    disabled={isSubmitting}
                                >
                                    {tCommon('cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    size="sm"
                                    disabled={isSubmitting || updateTranslationMutation.isPending}
                                >
                                    {isSubmitting || updateTranslationMutation.isPending
                                        ? t('updating')
                                        : t('updateTranslation')}
                                </Button>
                            </div>
                        </div>

                        {formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}

                        {/* Main Content */}
                        <div className="mt-6">
                            <TranslationForm />
                        </div>
                    </form>
                </FormProvider>
            </div>
        </div>
    )
}

export const TranslationDetailPage = withRequiredPermission(TranslationDetailPageInner, PERMISSIONS.STORES_VIEW)